/* eslint-disable react-refresh/only-export-components */
import { MultiStepDialogContent } from "@/components/custom/paginated-dialog-content/multi-step-dialog-content";
import React from "react";
import { useTranslation } from "react-i18next";
import { useJobDefinition } from "./-job-definitions-context";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  EditableDataProvider,
  OnSubmitProvider,
  useEditableData,
} from "@/components/custom/editable-data";
import {
  EditableDataScheduleStartProvider,
  ScheduleData,
  ScheduledJobsScheduleContent,
  ScheduledJobsScheduleOnce,
  useScheduleStart,
} from "./-scheduled-job-schedule";
import { cn, render } from "@/lib/utils";
import { EditableInput, EditableTextArea } from "./-job-inputs-common";
import { BsPlay } from "react-icons/bs";
import { PiCalendarBlank, PiPlus, PiTimer } from "react-icons/pi";
import { CiRepeat } from "react-icons/ci";
import { TbClockHour4, TbTemplate } from "react-icons/tb";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { functionalUpdate, useNavigate } from "@tanstack/react-router";
import {
  EditableDataTrainingScheduleStartProvider,
  TrainingScheduledJobsScheduleContent,
  TrainingScheduledJobsScheduleOnce,
  useTrainingScheduleStart,
} from "../training/schedule/$scheduledJobId/schedule/-schedule.tsx";
import { RecurringMode } from "../training/schedule/-recurrence.tsx";
import { Toggle } from "@/components/custom/toggle.tsx";
import { Button } from "@/components/custom/button.tsx";
import { Resolver, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CreateScheduledTrainingDto } from "@/client/index.ts";

import { showActionErrorToast } from "@/state/error-toast";
import { toast } from "sonner";
import { client, TrainingDefaultsDto } from "@/client";
import {
  pickTrainingDefaults,
  untouchedDefaultsAsNull,
} from "@/hooks/use-training-job-defaults";
import { useCurrentRef } from "@/hooks/use-current-ref";
import type { ScheduleStart } from "./-scheduled-job-schedule";

type JobCreationFormData = ScheduleData & {
  data?: any;
  name?: string;
  description?: string;
  runNow?: boolean;
};

export const JobCreationFormDataSchema = z
  .object({
    name: z.string().min(1),
    // A job or schedule saved without a description comes back from the API as null, and Copy seeds
    // the wizard from it. Accept it, and hand onSubmit undefined so the request leaves the field out,
    // the same as a new job whose description was left blank.
    description: z
      .string()
      .nullish()
      .transform((v) => v ?? undefined),
  })
  .passthrough();

/**
 * An opt-in extra wizard step. Job types that need more than the default
 * type/inputs/scheduler flow supply one of these instead of this file growing a
 * `path === "..."` branch per job type.
 *
 * `titleKey` rather than a resolved title so a descriptor can be a module-level
 * constant (stable identity in the `pages` memo) without the call site needing
 * `useTranslation`. `blocker` receives the form because descriptors live outside
 * React and `MultiStepDialogContent` invokes blockers with no arguments.
 */
export type JobCreationExtraStep = {
  titleKey: string;
  content: React.FC<any>;
  blocker?: (
    form: UseFormReturn<any>,
  ) => boolean | string | Promise<boolean | string>;
  scroll?: boolean;
};

export type JobCreationStateProps = {
  inputs?: React.FC<any>;
  /** Rendered after the scheduler step. */
  windowStep?: JobCreationExtraStep;
  /** Rendered last, so it becomes the submit page. */
  summaryStep?: JobCreationExtraStep;
  /**
   * Restricts the Scheduler step to these modes (in this order). Undefined keeps the default set.
   * A job type whose modes map to different permissions passes the ones the user actually holds.
   */
  allowedScheduleTypes?: readonly ScheduleStart[];
};

function useJobCreationState({
  // jobType,
  // editableJobType,
  inputs,
  windowStep,
  summaryStep,
  allowedScheduleTypes,
}: JobCreationStateProps) {
  const jobDefinition = useJobDefinition();
  const editableData = useEditableData<JobCreationFormData>();
  const [open, setOpen] = React.useState(false);
  const [step, setStep] = React.useState(0);
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: jobDefinition.root });
  const { t } = useTranslation();
  // read at submit time, not render time: it is what tells a prefilled value from an edited one
  const dirtyFieldsRef = useCurrentRef(editableData.form.formState.dirtyFields);
  const mutation = useMutation({
    mutationFn: async (data_: JobCreationFormData) => {
      if (jobDefinition.path === "training") {
        // `recurringMode` is UI-only state (which recurrence preset is selected), never sent, and
        // `saveAsAccountDefault` writes the account default rather than any part of this job
        const {
          runNow,
          name,
          description,
          recurring,
          recurringDays,
          recurringMode: _recurringMode,
          saveAsAccountDefault,
          scheduledFor,
          ...data
        } = data_ as CreateScheduledTrainingDto &
          JobCreationFormData & {
            recurringMode?: RecurringMode;
            saveAsAccountDefault?: boolean;
          };

        // What the defaults were before this submit, so the toast's Undo has something to restore.
        const previousDefaults = saveAsAccountDefault
          ? ((await queryClient.ensureQueryData(
              client.trainings.getDefaults.asQueryOptions(),
            )) as TrainingDefaultsDto)
          : undefined;

        /**
         * Runs *after* the job exists, and must never reject: the job is already created, so a
         * failure here has to be reported on its own rather than turning the whole mutation into
         * "Failed to create job" - which would offer a retry that creates a second job.
         */
        const saveDefault = async (): Promise<{
          saved: boolean;
          error?: unknown;
        }> => {
          if (!saveAsAccountDefault) return { saved: false };
          try {
            // a second request, not part of the create payload: rewriting what every other user in
            // the account starts from sits behind a different permission than creating a job
            await client.trainings.updateDefaults({
              requestBody: pickTrainingDefaults(data as any),
            } as any);
            await queryClient.invalidateQueries({
              queryKey: client.trainings.getDefaults.queryKey,
            });
            return { saved: true };
          } catch (error) {
            return { saved: false, error };
          }
        };

        if (runNow) {
          const result = await jobDefinition.actions.startJob({
            requestBody: { name, description, ...data },
          });
          const { saved: savedDefault, error: defaultError } =
            await saveDefault();
          return {
            type: "now",
            result,
            savedDefault,
            defaultError,
            previousDefaults,
          } as const;
        } else {
          // A schedule only stores what the user actually typed. Anything left as the prefilled
          // default is sent as null so it re-resolves on every run -- otherwise a recurring job
          // would freeze the container version that happened to be current in the wizard.
          const result = await jobDefinition.actions.createSchedule({
            requestBody: {
              name: name!,
              description,
              ...data,
              ...untouchedDefaultsAsNull(data, dirtyFieldsRef.current),
              ...{
                recurring: recurring ?? -1,
                // null, never [] - an empty list is not a state this field has
                recurringDays: recurringDays?.length ? recurringDays : null,
                scheduledFor,
              },
            },
          });
          const { saved: savedDefault, error: defaultError } =
            await saveDefault();
          return {
            type: "scheduled",
            result,
            savedDefault,
            defaultError,
            previousDefaults,
          } as const;
        }
      } else {
        const { runNow, name, description, data, ...scheduler } = data_;
        if (runNow) {
          const result = await jobDefinition.actions.startJob({
            requestBody: { name, description, ...data },
          });
          return { type: "now", result } as const;
        } else {
          const result = await jobDefinition.actions.createSchedule({
            requestBody: { name: name!, description, ...scheduler, data },
          });
          return { type: "scheduled", result } as const;
        }
      }
    },
    mutationKey: ["jobCreation"],
    onSuccess: ({ type, result, ...rest }) => {
      setOpen(false);
      queryClient.invalidateQueries(
        type === "scheduled"
          ? jobDefinition.actions.listScheduled
          : jobDefinition.actions.listJobs,
      );

      const { savedDefault, defaultError, previousDefaults } = rest as {
        savedDefault?: boolean;
        defaultError?: unknown;
        previousDefaults?: TrainingDefaultsDto;
      };
      // the job was created either way; only the account-default write failed, and saying so here
      // is what keeps the user from retrying the wizard and creating the job twice
      if (defaultError) {
        showActionErrorToast(defaultError, t("jobCreatedAccountDefaultFailed"));
      }
      if (savedDefault) {
        // the Toaster is app-level, so this survives the navigate below
        toast.success(t("jobCreatedAccountDefaultUpdated"), {
          id: "training-default-updated",
          action: {
            label: t("undo"),
            onClick: async () => {
              try {
                // no stored default before this submit -> undo means clearing it again, not
                // writing back a row of nulls
                if (previousDefaults?.isSet) {
                  await client.trainings.updateDefaults({
                    requestBody: pickTrainingDefaults(previousDefaults as any),
                  } as any);
                } else {
                  await client.trainings.clearDefaults();
                }
                await queryClient.invalidateQueries({
                  queryKey: client.trainings.getDefaults.queryKey,
                });
                toast.success(t("accountDefaultRestored"));
              } catch (err) {
                showActionErrorToast(err);
              }
            },
          },
        });
      }

      navigate({
        to: `${jobDefinition.root}/${type === "scheduled" ? "schedule" : "jobs"}/${result.id}`,
        search: { __clear: true } as any,
      });
    },
    onError: (err: any) => {
      showActionErrorToast(err, "Failed to create job");
    },
  });
  const onSubmit = mutation.mutateAsync;
  return {
    ...editableData,
    jobDefinition,
    mutation,
    onSubmit,
    open,
    setOpen,
    step,
    setStep,
    inputs,
    windowStep,
    summaryStep,
    allowedScheduleTypes,
  };
}

type JobCreationState = ReturnType<typeof useJobCreationState>;

const JobCreationContext = React.createContext<JobCreationState>(
  undefined as unknown as JobCreationState,
);

export function JobCreationProvider_({
  children,
  ...props
}: JobCreationStateProps & {
  children?: React.ReactNode;
}) {
  const value = useJobCreationState({ ...props });
  return (
    <JobCreationContext.Provider value={value}>
      <OnSubmitProvider value={value.onSubmit}>{children}</OnSubmitProvider>
    </JobCreationContext.Provider>
  );
}

export function JobCreationProvider({
  children,
  resolver = zodResolver(JobCreationFormDataSchema),
  ...props
}: JobCreationStateProps & { resolver?: Resolver<any> } & {
  children?: React.ReactNode;
}) {
  return (
    <EditableDataProvider<JobCreationFormData>
      data={{} as JobCreationFormData}
      editable={true}
      editMode={true}
      resolver={resolver}
    >
      <EditableDataScheduleStartProvider>
        <EditableDataTrainingScheduleStartProvider>
          <JobCreationProvider_ {...props}>{children}</JobCreationProvider_>
        </EditableDataTrainingScheduleStartProvider>
      </EditableDataScheduleStartProvider>
    </EditableDataProvider>
  );
}

export function useJobCreation() {
  return React.useContext(JobCreationContext);
}

export function JobCreationDialogContent() {
  const { t } = useTranslation();
  const { form, onSubmit } = useEditableData();
  const {
    step,
    setStep,
    windowStep,
    summaryStep,
    jobDefinition: { path },
  } = useJobCreation();
  const pages = React.useMemo(
    () => [
      {
        content: JobType,
        title: t("type"),
        blocker: () =>
          form.trigger(["name", "description"], {
            shouldFocus: true,
          }),
      },
      {
        content: Inputs,
        title: t("inputs"),
        blocker: () =>
          form.trigger(
            path === "training"
              ? [
                  "autoDeploy",
                  "clusterIds",
                  "serverDeviceId",
                  "inputModelId",
                  "clientImageUrl",
                  "serverImageUrl",
                  "config",
                ]
              : ["data"],
            {
              shouldFocus: true,
            },
          ),
        scroll: false,
      },
      {
        content: path === "training" ? TrainingScheduler : Scheduler,
        title: t("scheduler"),
        blocker: () =>
          form.trigger(
            path === "training"
              ? [
                  "isTrainingWindow",
                  "windowEnd",
                  "windowStart",
                  "recurring",
                  "recurringDays",
                  "scheduledFor",
                ]
              : [
                  "every",
                  "duration",
                  "timezone",
                  "cronStart",
                  "cronEnd",
                  "startDate",
                  "endDate",
                ],
            { shouldFocus: true },
          ),
      },
      ...[windowStep, summaryStep].filter(Boolean).map((extra) => ({
        content: extra!.content,
        title: t(extra!.titleKey),
        scroll: extra!.scroll,
        blocker: extra!.blocker ? () => extra!.blocker!(form) : undefined,
      })),
    ],
    [t, path, form, windowStep, summaryStep],
  );
  // Clamp rather than modulo: the page count now varies per job type, and a
  // wrapped index both renders a blank body (pages[-1]) and mis-computes
  // isLastStep, which would turn Submit back into Next.
  const lastStep = pages.length - 1;
  return (
    <MultiStepDialogContent
      title={t("createJob")}
      onSubmit={form.handleSubmit(onSubmit!)}
      currentStep={Math.min(Math.max(step, 0), lastStep)}
      onNext={() => setStep((s) => Math.min(s + 1, lastStep))}
      onBack={() => setStep((s) => Math.max(s - 1, 0))}
      //   onSubmit={() => onSubmit!(form.getValues())}
      pages={pages}
    />
  );
}

function JobType() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2 items-start w-full">
      {/* <FormItemContent className="w-full" label={t("jobType")} clear={null}>
        <JobTypeSelectInput className="w-full" />
      </FormItemContent> */}
      <EditableInput
        container={{ className: "w-full" }}
        name="name"
        label={t("jobName")}
        type="string"
        isUndefinable={true}
        className="w-full"
        fit={true}
        fitProps={{ className: "w-full overflow-x-auto -m-1 p-1" }}
      />
      <EditableTextArea
        container={{ className: "w-full" }}
        name="description"
        label={t("description")}
        isUndefinable={true}
        isNullable={true}
        className="w-full"
      />
    </div>
  );
}

function Inputs() {
  const {
    inputs,
    jobDefinition: { path },
  } = useJobCreation();
  return (
    <div className="w-full">
      {render(inputs, {
        prefix: path === "training" ? undefined : "data",
        className: "w-full",
        container: { className: "w-full" },
      })}
    </div>
  );
}

function SelectBox({
  isSelected,
  setIsSelected,
  isDisabled,
  children,
}: {
  isDisabled?: boolean;
  isSelected: boolean;
  setIsSelected: React.Dispatch<React.SetStateAction<boolean>>;
  children?: React.ReactNode;
}) {
  return (
    <div
      onClick={(_ev) => setIsSelected(true)}
      className={cn(
        "border p-2 rounded-md flex items-center justify-between gap-2 w-full",
        isSelected && "border-azure bg-azure/10",
      )}
    >
      <div className="flex items-center gap-2">
        <Toggle
          variant={"round_dot"}
          size="xs"
          pressed={isSelected}
          onPressedChange={() => setIsSelected(true)}
          disabled={isDisabled}
        />
        {children}
      </div>
    </div>
  );
}

const useScheduleAsIsSelected = <T,>({
  value,
  defaultValue = undefined as unknown as T,
  schedule,
  setSchedule,
}: {
  value: T;
  defaultValue?: T;
  schedule: T;
  setSchedule: React.Dispatch<React.SetStateAction<T>>;
}) => {
  const isSelected = schedule === value;
  const setIsSelected = (a: React.SetStateAction<boolean>) =>
    setSchedule((s) => {
      const prev = s === value;
      const next = functionalUpdate(a, prev);
      if (next === prev) return s;
      if (next) {
        return value;
      } else {
        return defaultValue;
      }
    });
  return { isSelected, setIsSelected };
};

function Scheduler() {
  const { t } = useTranslation();
  const { schedule, setSchedule } = useScheduleStart();
  const {
    jobDefinition: { path },
    allowedScheduleTypes,
  } = useJobCreation();
  const showInterval =
    path === "service-health-checks" || path === "capacity-tagging";
  const allowed = React.useMemo<readonly ScheduleStart[]>(
    () =>
      allowedScheduleTypes ?? [
        "runNow",
        "once",
        "recurring",
        ...(showInterval ? (["interval"] as const) : []),
        "draft",
      ],
    [allowedScheduleTypes, showInterval],
  );
  // The scheduler state starts on a default mode that may not be offered (e.g. a user who may only
  // run jobs immediately). Move to the first offered mode rather than leave a hidden one selected.
  React.useEffect(() => {
    if (allowed.length > 0 && !allowed.includes(schedule)) {
      setSchedule(allowed[0]);
    }
  }, [allowed, schedule, setSchedule]);
  // Hooks run unconditionally; only the rendering is conditional.
  const runNowSelected = useScheduleAsIsSelected({
    value: "runNow",
    schedule,
    setSchedule,
  });
  const onceSelected = useScheduleAsIsSelected({
    value: "once",
    schedule,
    setSchedule,
  });
  const recurringSelected = useScheduleAsIsSelected({
    value: "recurring",
    schedule,
    setSchedule,
  });
  const intervalSelected = useScheduleAsIsSelected({
    value: "interval",
    schedule,
    setSchedule,
  });
  const draftSelected = useScheduleAsIsSelected({
    value: "draft",
    schedule,
    setSchedule,
  });
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex flex-col items-center gap-2">
        <TbClockHour4 className="size-8" />
        <div className="flex flex-col items-center">
          <h1 className="text-base">{t("whenShouldThisJobRun?")}</h1>
          <p className="text-foreground/60 text-sm">
            {t("configureTheScheduleForJobExecution")}
          </p>
        </div>
      </div>
      {allowed.includes("runNow") && (
        <SelectBox {...runNowSelected}>
          <span className="flex gap-2 items-center">
            <BsPlay />
            <span>{t("runImmediately")}</span>
          </span>
        </SelectBox>
      )}
      {allowed.includes("once") && (
        <SelectBox {...onceSelected}>
          <span className="flex gap-2 items-center">
            <PiCalendarBlank />
            <span>{t("scheduleForASpecificTime")}</span>
          </span>
        </SelectBox>
      )}
      {allowed.includes("recurring") && (
        <SelectBox {...recurringSelected}>
          <span className="flex gap-2 items-center">
            <CiRepeat />
            <span>{t("recurringSchedule")}</span>
          </span>
        </SelectBox>
      )}
      {allowed.includes("interval") && (
        <SelectBox {...intervalSelected}>
          <span className="flex gap-2 items-center">
            <PiTimer />
            <span>{t("interval")}</span>
          </span>
        </SelectBox>
      )}
      {allowed.includes("draft") && (
        <SelectBox {...draftSelected}>
          <span className="flex gap-2 items-center">
            <TbTemplate />
            <span>{t("draft")}</span>
          </span>
        </SelectBox>
      )}
      {schedule === "once" ? (
        <>
          <ScheduledJobsScheduleOnce />
        </>
      ) : schedule === "recurring" ? (
        <>
          <ScheduledJobsScheduleContent />
        </>
      ) : schedule === "interval" ? (
        <>
          <ScheduledJobsScheduleContent />
        </>
      ) : undefined}
    </div>
  );
}

function TrainingScheduler() {
  const { t } = useTranslation();
  const { schedule, setSchedule } = useTrainingScheduleStart();
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex flex-col items-center gap-2">
        <TbClockHour4 className="size-8" />
        <div className="flex flex-col items-center">
          <h1 className="text-base">{t("whenShouldThisJobRun?")}</h1>
          <p className="text-foreground/60 text-sm">
            {t("configureTheScheduleForJobExecution")}
          </p>
        </div>
      </div>

      <SelectBox
        {...useScheduleAsIsSelected({ value: "runNow", schedule, setSchedule })}
      >
        <span className="flex gap-2 items-center">
          <BsPlay />
          <span>{t("runImmediately")}</span>
        </span>
      </SelectBox>
      <SelectBox
        {...useScheduleAsIsSelected({ value: "once", schedule, setSchedule })}
      >
        <span className="flex gap-2 items-center">
          <PiCalendarBlank />
          <span>{t("scheduleForASpecificTime")}</span>
        </span>
      </SelectBox>
      <SelectBox
        {...useScheduleAsIsSelected({
          value: "interval",
          schedule,
          setSchedule,
        })}
      >
        <span className="flex gap-2 items-center">
          <CiRepeat />
          <span>{t("recurringSchedule")}</span>
        </span>
      </SelectBox>
      <SelectBox
        {...useScheduleAsIsSelected({
          value: "draft",
          schedule,
          setSchedule,
        })}
      >
        <span className="flex gap-2 items-center">
          <TbTemplate />
          <span>{t("draft")}</span>
        </span>
      </SelectBox>
      {schedule === "once" ? (
        <>
          <TrainingScheduledJobsScheduleOnce />
        </>
      ) : schedule === "interval" ? (
        <>
          <TrainingScheduledJobsScheduleContent />
        </>
      ) : undefined}
    </div>
  );
}

// function Summary() {
//   return <></>;
// }

export function JobCreationDialog() {
  const { open, setOpen } = useJobCreation();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <JobCreationDialogContent />
      </DialogContent>
    </Dialog>
  );
}

export function CreateJobButton() {
  const { t } = useTranslation();
  const { setOpen, setStep, form } = useJobCreation();
  return (
    <Button
      variant={"azure"}
      onClick={(_ev) => {
        setStep(0);
        form.reset(undefined, {
          keepDefaultValues: false,
          keepDirty: false,
          keepDirtyValues: false,
          keepErrors: false,
          keepIsSubmitSuccessful: false,
          keepIsSubmitted: false,
          keepIsValid: false,
          keepIsValidating: false,
          keepSubmitCount: false,
          keepTouched: false,
          keepValues: false,
        });
        setOpen(true);
      }}
    >
      <PiPlus />
      {t("addNewJob")}
    </Button>
  );
}
